import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  AttributeValue,
  AuthService,
  DmcService,
  EditedAttributeValues,
  EmcOptions,
  EntityModelCustomizationService,
  PermissionsService,
  Purpose,
  SectionDefinitionEntityType,
  SectionInstance,
  getAttributeEdit,
  slideToggle,
} from 'shared-lib';
import { DeferredToastService } from 'src/app/services/deferred-toast.service';
import {
  ClientType,
  Customer,
  CustomerEdit,
  CustomerType,
  Neighborhood,
} from 'src/app/models/customer.model';
import { ImageType } from 'src/app/models/image.model';
import { User } from 'src/app/models/user.model';
import { BuildingService } from 'src/app/services/building.service';
import { CustomerService } from 'src/app/services/customer.service';
import { MeterService } from 'src/app/services/meter.service';
import { UserService } from 'src/app/services/user.service';
import { HistoryOutput } from 'src/app/models/history.model';
import { NextDataQuality } from 'src/app/models/dataQuality.model';
import { RecallType } from 'src/app/models/recallType.model';
import { AssetStatusEnum } from 'src/app/models/assetStatus.model';
import { EnergySource, MeterType } from 'src/app/models/meter.model';
import { OptionObj } from 'src/app/models/util.model';
import { KeyValuePair } from 'src/app/models/keyValueItem.model';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { BlobStorageService } from 'src/app/_modules/shared/_services/blob-storage.service';
import { ContainerNames } from 'src/app/models/containerNames.model';

type CustomerStaticProperty =
  | 'TenantName'
  | 'BuildingOwner'
  | 'CardNumber'
  | 'PhoneNumber'
  | 'Email'
  | 'CustomerInspectionConclusion'
  | 'NumberOfHouseholds'
  | 'PermiseType'
  | 'PermiseCategory'
  | 'Activity'
  | 'SubActivity'
  | 'Neighborhood'
  | 'NumberOfRooms'
  | 'MeterType'
  | 'MeterNo'
  | 'CustomerType'
  | 'SltStatus'
  | 'ClientType'
  | 'FrontViewPictures'
  | 'BuildingTagMountedPicture'
  | 'BillPicture'
  | 'IdCardPicture'
  | 'CustomerTagPicture'
  | 'MeterNumberPicture'
  | 'CardBackViewPicture'
  | 'NewMeterPicture';

@Component({
  selector: 'app-edit-dynamic-customer',
  templateUrl: './edit-dynamic-customer.component.html',
  styleUrls: ['./edit-dynamic-customer.component.scss'],
  animations: [slideToggle],
})
export class EditDynamicCustomerComponent implements OnInit {
  @ViewChild('closeModal') closeModal: ElementRef;
  @ViewChild('closeDQModal') closeDQModal: ElementRef;
  @ViewChild('closeReviewModal') closeReviewModal: ElementRef;
  @ViewChild('openUpdateDescriptionModal') openUpdateDescriptionModal: ElementRef;
  @ViewChild('dropdownContainer') dropdownContainer: ElementRef;
  @ViewChild('userFilterInput') userFilter: ElementRef;

  AssetAssignType = AssetStatusEnum;
  assetAssignType: AssetStatusEnum;
  ImageType = ImageType;

  guid: string = '';
  customerId: number = 0;
  customer: CustomerEdit = new CustomerEdit();
  customerHeaderInfo: Customer = new Customer();
  sectionInstances: SectionInstance[] = [];
  editableAttributeIds: number[] = [];
  editableStaticProperties: string[] = [];
  editedValues: EditedAttributeValues = {};
  originalMeterNumber: string = '';
  mapLatitude?: number;
  mapLongitude?: number;
  hasCimDataIntegration: boolean = false;
  timeZone: any;

  customerInfoCardExpanded: boolean = true;
  buildingInfoCardExpanded: boolean = true;
  networkInfoCardExpanded: boolean = true;
  energySourceCardExpanded: boolean = true;
  meterInfoCardExpanded: boolean = true;
  userActivityCardExpanded: boolean = true;
  imagesCardExpanded: boolean = true;

  editEnabled: boolean = false;
  editFieldsPopulated: boolean = false;
  onlyView: boolean = false;
  isDataQuality: boolean = false;
  updateStatusClicked: boolean = false;
  showRecallMenu: boolean = false;
  recallAssignedAllowed: boolean = false;
  recallValidatedAllowed: boolean = false;
  modalRejectionReason?: string = '';
  history: HistoryOutput = new HistoryOutput();
  billingCalled: boolean = false;

  isCinEnabled: boolean = localStorage.getItem('defaultCaimsCodePatterns') === 'false';
  isPropertyCodeShort: boolean = true;
  isElectricalAddressLong: boolean = false;
  isMapVisible: boolean = false;

  mobileUsers: User[] = [];
  assignedUserId: number = 0;
  tempAssignedUserId: number = 0;
  filterText: string = '';
  isDropdownOpened: boolean = false;

  selectedImage: any;
  isImageMapVisible: boolean = false;
  imgElement: any;
  imgZoomFullContainer: any;
  currentRotation: number = 0;

  currentEnergySourcePage = 0;
  currentEnergySource: EnergySource;
  countOfEnergySource = 0;

  // Fields visibility props
  isGhanaPostFieldsVisible: boolean = true;
  isGeoCodeVisible: boolean = true;
  isSPNFieldVisible: boolean = true;
  isServiceTypeFieldVisible: boolean = true;
  isActualTariffFieldVisible: boolean = true;
  isMeterSlrnFieldVisible: boolean = true;
  isImageMeterFieldVisible: boolean = true;
  isBackViewPictureFieldVisible: boolean = true;
  isNewMeterPictureVisible: boolean = true;
  isRegularizationUploadVisible: boolean = true;
  isCustomerMountedTagVisible: boolean = true;
  isFrmMeterVisible: boolean = true;
  isInstallationDateVisible: boolean = true;
  isReplacementDateVisible: boolean = true;

  // Edit options
  sltOptions: OptionObj[] = [];
  selectedSltStatus: OptionObj = { key: 0, value: '' };
  meterTypeOptions: OptionObj[] = [];
  customerTypeOptions: OptionObj[] = [];
  clientTypeOptions: OptionObj[] = [];
  neighborhoodOptions: OptionObj[] = [];
  neighborhood = Neighborhood;
  numberOfRoomsOptions: number[];
  activityList: KeyValuePair[] = [];
  permiseList: KeyValuePair[] = [];
  activityOptions: string[] = [];
  permiseOptions: string[] = [];
  subactivityOptions: string[] = [];
  permiseCategoryOptions: string[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private customerService: CustomerService,
    private toastr: ToastrService,
    private deferredToastService: DeferredToastService,
    private meterService: MeterService,
    private buildingService: BuildingService,
    private userService: UserService,
    public permissionsService: PermissionsService,
    private router: Router,
    private el: ElementRef,
    private analyticsService: AnalyticsService,
    private blobStorageService: BlobStorageService,
    private renderer: Renderer2,
    private authService: AuthService,
    private emcService: EntityModelCustomizationService,
    private dmcService: DmcService
  ) {
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
  }

  ngOnInit(): void {
    this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.setFieldsVisibility();
    const path = this.activatedRoute.snapshot.url[0].path;
    this.onlyView = path == 'report-view';
    this.isDataQuality = path == 'data-quality';
    this.guid = this.activatedRoute.snapshot.paramMap.get('guid') || '';
    this.imgElement = this.el?.nativeElement?.querySelector(
      '.ngxImageZoomThumbnail'
    );
    this.imgZoomFullContainer =
      this.el?.nativeElement?.querySelector('.ngxImageZoomFull');
    this.getOptions();
    this.getActivities();
    this.getPermiseTypes();
    this.getCustomer(this.guid);
    this.getSectionInstances(this.guid);
    this.getEditEmcOptions();
    this.blobStorageService.setContainer(ContainerNames.Images);
    this.deferredToastService.showQueuedMessage();
  }

  private getCustomer(guid: string) {
    this.customerService.getByGuid(guid).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.customer = new CustomerEdit(responseData.data);
          this.customerId = this.customer.id;
          this.assignedUserId = this.customer.building.assignedUserId ?? 0;
          this.customer.feederId = this.customer.dt?.feeder?.id;
          this.customer.dtId = this.customer.dt ? this.customer.dt.id : 0;
          this.customer.meterTypeId = this.customer.meter?.meterType
            ? this.customer.meter?.meterType?.id
            : 0;
          this.customer.meterNo = this.customer.meter
            ? this.customer.meter.number
            : '';
          this.originalMeterNumber = this.customer.meter
            ? this.customer.meter.number
            : '';
          this.filterText = this.customer.building.assignedUser ?? '';
          this.tempAssignedUserId = this.assignedUserId;
          this.customer.closestLandmark = this.customer.building?.closestLandmark;
          this.customer.spn = this.customer.contractNumber;
          this.formatHeaderInfo(this.customer);
          this.selectedSltStatus.key = this.customer.sltStatus?.id;
          this.selectedSltStatus.value = this.customer.sltStatus?.value;
          this.countOfEnergySource = this.customer.energySources?.length ?? 0;

          if (this.countOfEnergySource) {
            this.currentEnergySource = this.customer.energySources[0];
          }
          if (
            this.customer.dataValidationReview != null &&
            this.customer.dataValidationReview.length > 0
          ) {
            this.modalRejectionReason = this.customer.dataValidationReview.find(
              (x) => x.key == 'Reason for rejection'
            )?.value;
          }
        } else {
          this.toastr.error(responseData?.message);
        }

        if (
          this.permissionsService.isCustomerRecallAssignedAllowed &&
          !(
            this.customer.dataQualityStatus?.toString() != 'Rejected' &&
            this.customer.building.status != 'Assigned'
          )
        ) {
          this.recallAssignedAllowed = true;
        }

        if (
          this.permissionsService.isCustomerRecallValidatedAllowed &&
          this.customer.dataQualityId != 0
        ) {
          this.recallValidatedAllowed = true;
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getSectionInstances(guid: string) {
    this.customerService.getSectionInstances(guid).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sectionInstances = (responseData.data ?? []).map((section) => ({
            ...section,
            expanded: true,
          }));
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => {
        this.toastr.error('Error fetching section instances');
      },
    });
  }

  private getEditEmcOptions() {
    const utilityId = parseInt(localStorage.getItem('utilityId') || '0');
    this.emcService
      .getSelectedEmcOptions(SectionDefinitionEntityType.Customer, Purpose.Edit, utilityId)
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            const options = (responseData.data ?? []) as EmcOptions[];
            this.editableAttributeIds = options
              .filter((option) => option.attributeDefinitionId)
              .map((option) => option.attributeDefinitionId!);
            this.editableStaticProperties = options
              .filter((option) => option.staticPropertyName)
              .map((option) => option.staticPropertyName!);
          } else {
            this.toastr.error(responseData?.message || 'Failed to fetch EMC options');
          }
        },
        error: () => {
          this.toastr.error('Error occurred while fetching EMC options');
        },
      });
  }

  isStaticEditable(property: CustomerStaticProperty): boolean {
    return this.editEnabled && this.editableStaticProperties.includes(property);
  }

  get isPermiseTypeEditable(): boolean {
    return this.isStaticEditable('PermiseType') && this.isStaticEditable('PermiseCategory');
  }

  get isActivityEditable(): boolean {
    return this.isStaticEditable('Activity') && this.isStaticEditable('SubActivity');
  }

  showCustomerLocation() {
    this.mapLatitude = this.customer.building.latitude;
    this.mapLongitude = this.customer.building.longitude;
    this.isMapVisible = !this.isMapVisible;
  }

  setClickedLocation(attributeValue: AttributeValue) {
    this.mapLatitude = attributeValue.latitude;
    this.mapLongitude = attributeValue.longitude;
    this.isMapVisible = !this.isMapVisible;
  }

  formatHeaderInfo(customer: CustomerEdit) {
    this.customerHeaderInfo.tenantName = customer.tenantName;
    this.customerHeaderInfo.slrn = customer.building.slrn;
    this.customerHeaderInfo.email = customer.email;
    this.customerHeaderInfo.phoneNumber = customer.phoneNumber;
    this.customerHeaderInfo.physicalAddress = customer.physicalAddress;
  }

  setModalType(type: AssetStatusEnum) {
    this.assetAssignType = type;
  }

  regularize() {
    this.customerService.regularizeCustomer(this.customerId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.deferredToastService.queueSuccess('Successfully regularized customer');
          window.location.reload();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  // Edit / save

  edit() {
    this.editEnabled = true;
    if (!this.editFieldsPopulated) {
      this.getSltStatusesForOptions();
      this.getMeterTypesForOptions();
      this.setDependant();
      this.editFieldsPopulated = true;
    }
  }

  openEditModal() {
    if (getAttributeEdit(this.sectionInstances, this.editableAttributeIds, this.editedValues).invalid) {
      this.toastr.warning('Please correct the invalid fields.');
      return;
    }

    const meterNumber = (this.customer.meter?.number ?? '').trim();
    const meterNumberChanged =
      meterNumber !== (this.originalMeterNumber ?? '').trim();

    if (meterNumber && meterNumberChanged) {
      this.customerService.checkMeterNumberExists(meterNumber).subscribe({
        next: (response) => {
          const responseData = response.body;
          if (
            (responseData?.status === 200 || responseData?.status === 'OK') &&
            responseData?.data === true
          ) {
            this.toastr.error('Customer with Meter Number already exists.');
          } else {
            this.openUpdateDescriptionModal?.nativeElement?.click();
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
    } else {
      this.openUpdateDescriptionModal?.nativeElement?.click();
    }
  }

  updateHistory(history: any) {
    this.history = history;
    this.customer.history = this.history;
    this.save();
  }

  save() {
    if (!this.validateEdit()) {
      return;
    }
    if (this.customer.meter.meterType == null) {
      this.customer.meter.meterType = new MeterType();
      this.customer.meter.meterType.id = this.customer.meterTypeId;
    } else {
      this.customer.meter.meterType.id = this.customer.meterTypeId;
      this.customer.meter.lastUpdateTime = null;
    }
    if (
      this.customer.sltStatus.id !== this.selectedSltStatus.key &&
      this.selectedSltStatus.key !== 0
    ) {
      this.customer.sltStatus.id = this.selectedSltStatus.key;
      this.customer.sltStatus.value = this.selectedSltStatus.value;
    }
    if (!this.customer.accountNumber) this.customer.accountNumber = null;

    this.analyticsService.logCustomEvent('CUSTOMER DETAILS: Edit', {
      customer_id: this.customer.id,
    });

    const attributeEdit = getAttributeEdit(this.sectionInstances, this.editableAttributeIds, this.editedValues);
    if (attributeEdit.changed.length === 0) {
      this.saveCustomer();
      return;
    }

    this.dmcService
      .updateAttributeValues({
        assetType: SectionDefinitionEntityType.Customer,
        assetGuid: this.guid,
        attributeValues: attributeEdit.changed,
      })
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.saveCustomer();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  private saveCustomer() {
    this.customerService.addCustomer(this.customer).subscribe({
      next: (response) => {
        const responseData = response.body;
        this.openUpdateDescriptionModal?.nativeElement?.click();
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.editEnabled = false;
          this.deferredToastService.queueSuccess('Successfully updated record. Status reverted to Pending.');
          window.location.reload();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  validateEdit() {
    const dtMissing = this.hasCimDataIntegration
      ? !this.customer.substation?.id
      : !this.customer.dtId;
    if (dtMissing) {
      this.toastr.error('DT field is mandatory');
      return false;
    }
    return true;
  }

  getMeterTypesForOptions() {
    this.meterService.getMeterTypesForOptions().subscribe({
      next: (response) => {
        this.meterTypeOptions = response.body.data;
      },
      error: (_) => {
        this.toastr.error('An error occurred.');
      },
    });
  }

  getSltStatusesForOptions() {
    this.customerService.getSltStatusesForOptions().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltOptions = responseData.data;
        } else if (response?.status != 204) {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getPermiseTypes() {
    this.customerService.getPermises().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.permiseList = responseData.data;
          this.permiseOptions = [
            ...new Set(this.permiseList.map((item) => item.key)),
          ];
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getActivities() {
    this.customerService.getActivities().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.activityList = responseData.data;
          this.activityOptions = [
            ...new Set(this.activityList.map((item) => item.key)),
          ];
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getOptions() {
    this.neighborhoodOptions = this.enumToOptions(Neighborhood);
    this.customerTypeOptions = this.enumToOptions(CustomerType);
    this.clientTypeOptions = this.enumToOptions(ClientType).filter(
      (x) => x.key != -1
    );
    this.numberOfRoomsOptions = [...Array(50).keys()].map((x) => ++x);
  }

  private enumToOptions(enumType: any): OptionObj[] {
    return Object.values(enumType)
      .filter((v) => !isNaN(Number(v)))
      .map((key) => ({ key: Number(key), value: enumType[key as number] }));
  }

  selectPermiseType() {
    this.customer.permiseCategory = undefined;
    this.permiseCategoryOptions = this.getPermiseCategories();
  }

  selectActivity() {
    this.customer.subActivity = undefined;
    this.subactivityOptions = this.getSubActivities();
  }

  setDependant() {
    if (this.customer.activity != undefined)
      this.subactivityOptions = this.getSubActivities();
    if (this.customer.permiseType != undefined)
      this.permiseCategoryOptions = this.getPermiseCategories();
  }

  private getSubActivities() {
    return this.activityList
      .filter((p) => p.key == this.customer.activity)
      .map((item) => item.value);
  }

  private getPermiseCategories() {
    return this.permiseList
      .filter((p) => p.key === this.customer.permiseType)
      .map((item) => item.value);
  }

  // Assign / retag

  getMobileUsers() {
    const areaId = this.hasCimDataIntegration
      ? this.customer.subGeographicalRegionId ?? 0
      : this.customer.area.id;

    this.userService.getMobileUsers(areaId, this.filterText).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.mobileUsers = responseData.data;
          this.isDropdownOpened = this.mobileUsers.length > 0;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  filterMobileUsers() {
    this.isDropdownOpened = true;
    this.tempAssignedUserId = 0;
    this.getMobileUsers();
  }

  openDropdown() {
    this.isDropdownOpened = true;
  }

  selectFieldAgent(id: number) {
    this.isDropdownOpened = false;
    this.assignedUserId = id;
    this.tempAssignedUserId = id;
    this.filterText = this.mobileUsers.find((x) => x.id == id)!.fullName;
  }

  assignTask(): void {
    const user = this.mobileUsers.find(
      (x) => x.fullName == this.filterText.trim()
    );
    if (user) {
      this.assignedUserId = user.id;
      this.tempAssignedUserId = user.id;
    } else {
      this.tempAssignedUserId = 0;
    }
    if (this.tempAssignedUserId == 0) {
      this.toastr.warning('Please select valid user.');
      return;
    }

    this.analyticsService.logCustomEvent('CUSTOMER DETAILS: Assign/Retag task', {
      slrn: this.customer.building.slrn,
    });

    this.buildingService
      .assignBuilding(
        this.customer.building.id,
        this.assignedUserId,
        this.assetAssignType,
        this.isDataQuality
      )
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.closeModal?.nativeElement?.click();
            this.deferredToastService.queueSuccess('Successfully assigned user');
            window.location.reload();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  onModalAssignFade() {
    this.filterText = this.customer.building.assignedUser ?? '';
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    const clickedInside =
      this.isDropdownOpened &&
      (this.dropdownContainer?.nativeElement?.contains(event.target) ||
        this.userFilter?.nativeElement?.contains(event.target));
    if (!clickedInside) {
      this.isDropdownOpened = false;
    }
  }

  // Recall

  recallAssigned() {
    this.buildingService
      .recall(this.customer.building.id, RecallType.Assigned)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.deferredToastService.queueSuccess('Successfully recalled customer.');
            window.location.reload();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  recallValidation() {
    this.customerService.recallDataQuality(this.customer.id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.deferredToastService.queueSuccess("Successfully recalled customer's Data Quality.");
          window.location.reload();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  // Status / data quality / audit / review

  disableUpdateStatusButton() {
    return this.customer.dataQualityId !== 0;
  }

  disableAuditReviewButton() {
    return this.customer.auditedStatus === 'Audited';
  }

  disableReviewButton() {
    return (
      this.customer.reviewStatus === 'Reviewed' || !this.customer.dataQualityId
    );
  }

  isComplete(): boolean {
    return (
      this.disableUpdateStatusButton() &&
      this.customer.dataQualityStatus?.toString() == 'Approved' &&
      this.customer.assetStatus !== 'Assigned'
    );
  }

  isRejected(): boolean {
    return (
      this.disableUpdateStatusButton() &&
      this.customer.dataQualityStatus?.toString() == 'Rejected' &&
      this.customer.assetStatus !== 'Assigned'
    );
  }

  isPending(): boolean {
    return (
      !this.disableUpdateStatusButton() &&
      this.customer.assetStatus !== 'Assigned'
    );
  }

  isAssigned(): boolean {
    return this.customer.assetStatus === 'Assigned';
  }

  getCustomerStatusTranslationKey(): string {
    if (this.isComplete()) return 'Complete';
    if (this.isRejected()) return 'Rejected';
    if (this.isAssigned()) return 'Assigned';
    return 'Pending';
  }

  closeRedirectModal() {
    this.closeDQModal?.nativeElement?.click();
  }

  cancelModal(event: any) {
    this.closeDQModal?.nativeElement?.click();
    this.routeToNextCustomerOrBuilding(event);
  }

  cancel(event: any) {
    if (!event) {
      return;
    }
    this.routeToNextCustomerOrBuilding(event);
  }

  cancelReviewModal(event: any) {
    this.closeReviewModal?.nativeElement?.click();
    this.routeToNextCustomerOrBuilding(event);
  }

  routeToNextCustomerOrBuilding(data: NextDataQuality) {
    if (data.isBuilding) {
      this.router.navigateByUrl(`/buildings/edit/${data.buildingGuid}`);
    } else if (data.isOnboard) {
      this.router.navigateByUrl(`/customers/edit/${data.customerGuid}`);
    } else {
      this.router.navigateByUrl(`/customers/edit/${data.customerGuid}`).then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
    }
  }


  getPaymentAndVendFromBilling() {
    if (this.billingCalled) {
      return;
    }
    const obj = {
      accountNumber: this.customer.accountNumber,
      meterNumber: this.customer.meter?.number,
      customerType: this.customer.customerType,
    };

    this.customerService.getPaymentAndVendFromBilling(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.billingCalled = true;
          this.customer.lastPaymentAmount = responseData.data.lastPaymentAmount;
          this.customer.lastPaymentDate = responseData.data.lastPaymentDate;
          this.customer.currentDebtBalance = responseData.data.currentDebtBalance;
          this.customer.lastVendAmount = responseData.data.lastVendAmount;
          this.customer.lastVendDate = responseData.data.lastVendDate;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  // Display helpers

  switchPropertyCodeFormat() {
    this.isPropertyCodeShort = !this.isPropertyCodeShort;
  }

  switchElectricalAddressFormat() {
    this.isElectricalAddressLong = !this.isElectricalAddressLong;
  }

  viewConnectedAssets(type: any) {
    switch (type) {
      case 1:
        this.router.navigate([`poles/edit/${this.customer.building.poleId}`]);
        break;
    }
  }

  previousEnergySource() {
    if (this.currentEnergySourcePage > 0) {
      this.currentEnergySourcePage--;
      this.currentEnergySource =
        this.customer.energySources[this.currentEnergySourcePage];
    }
  }

  nextEnergySource() {
    if (this.currentEnergySourcePage < this.customer.energySources.length - 1) {
      this.currentEnergySourcePage++;
      this.currentEnergySource =
        this.customer.energySources[this.currentEnergySourcePage];
    }
  }

  setFieldsVisibility() {
    const stored = localStorage.getItem('notVisibleFields');
    if (!stored) {
      return;
    }
    const notVisibleFields: string[] = JSON.parse(stored);
    const isVisible = (field: string) => !notVisibleFields.includes(field);

    this.isGhanaPostFieldsVisible = isVisible('ghanaPostCode');
    this.isGeoCodeVisible = isVisible('geoCode');
    this.isActualTariffFieldVisible = isVisible('actualTariff');
    this.isSPNFieldVisible = isVisible('spn');
    this.isServiceTypeFieldVisible = isVisible('serviceType');
    this.isMeterSlrnFieldVisible = isVisible('meterSlrn');
    this.isImageMeterFieldVisible = isVisible('imageMeter');
    this.isBackViewPictureFieldVisible = isVisible('backViewPicture');
    this.isRegularizationUploadVisible = isVisible('regularizationUpload');
    this.isFrmMeterVisible = isVisible('frmMeterNumber');
    this.isInstallationDateVisible = isVisible('installationDate');
    this.isReplacementDateVisible = isVisible('replacementDate');
    this.isNewMeterPictureVisible = isVisible('newMeterPicture');
    this.isCustomerMountedTagVisible = isVisible('customerTagMountedPicture');
  }

  // Images

  getBuildingImageByType(imageType: string, index?: number) {
    return this.customer.buildingImages.filter((x) => x.imageType === imageType)[index ?? 0];
  }

  getCustomerImageByType(imageType: string) {
    return this.customer.customerImages.find((x) => x.imageType === imageType);
  }

  getBuildingMountedImage() {
    return this.customer.buildingImages.find(
      (x) => x.imageType === 'MountedTag'
    )?.url;
  }

  getFrontViewImage(id: number) {
    return this.customer.buildingImages.filter(
      (x) => x.imageType === 'Frontview'
    )[id]?.url;
  }

  showImage(type: ImageType, num?: number) {
    switch (type) {
      case ImageType.Frontview:
        this.selectedImage = this.getBuildingImageByType('Frontview', num!);
        break;
      case ImageType.MountedTag:
        this.selectedImage = this.getBuildingImageByType('MountedTag');
        break;
      case ImageType.Bill:
        this.selectedImage = this.getCustomerImageByType('Bill');
        break;
      case ImageType.Card:
        this.selectedImage = this.getCustomerImageByType('Card');
        break;
      case ImageType.Meter:
        this.selectedImage = this.getCustomerImageByType('Meter');
        break;
      case ImageType.MeterNumber:
        this.selectedImage = this.getCustomerImageByType('MeterNumber');
        break;
      case ImageType.CardBackViewImage:
        this.selectedImage = this.getCustomerImageByType('CardBackViewImage');
        break;
      case ImageType.MeterReplacementReadingImage:
        this.selectedImage = this.getCustomerImageByType('MeterReplacementReadingImage');
        break;
    }
  }

  fileBrowserHandler({ file, type, imageId }: { file: File; type: ImageType; imageId: number }) {
    if (!file) {
      this.toastr.error('Image data is missing');
      return;
    }

    if (this.isCustomerImageType(type)) {
      this.handleCustomerImageUpload(file, type, imageId);
    } else {
      this.handleBuildingImageUpload(file, type, imageId);
    }
  }

  private isCustomerImageType(type: ImageType): boolean {
    return (
      type === ImageType.Bill ||
      type === ImageType.Card ||
      type === ImageType.Meter ||
      type === ImageType.MeterNumber ||
      type === ImageType.CardBackViewImage
    );
  }

  private handleCustomerImageUpload(file: File, type: ImageType, imageId: number) {
    this.blobStorageService.uploadFile(
      file,
      file.name,
      (uploadedUrl) => {
        const obj = {
          imageType: type,
          customerId: this.customer.id,
          guid: this.customer.guid,
          url: uploadedUrl,
          imageId: imageId,
        };

        this.customerService.uploadPicture(obj, file, this.customer.guid).subscribe({
          next: (response) => this.handleCustomerUploadResponse(response, type),
          error: (_) => this.toastr.error('Error occurred while uploading to the server'),
        });
      },
      (error) => {
        console.error('Error uploading image to blob storage:', error);
        this.toastr.error('Failed to upload image to storage');
      }
    );
  }

  private handleBuildingImageUpload(file: File, type: ImageType, imageId: number) {
    this.blobStorageService.uploadFile(
      file,
      file.name,
      (uploadedUrl) => {
        const obj = {
          imageType: type,
          buildingId: this.customer.building.id,
          guid: this.customer.building.guid,
          url: uploadedUrl,
          imageId: imageId,
        };

        this.buildingService.uploadPicture(obj, file).subscribe({
          next: (response) => this.handleBuildingUploadResponse(response, type, imageId),
          error: (_) => this.toastr.error('Error occurred while uploading to the server'),
        });
      },
      (error) => {
        console.error('Error uploading building image to blob storage:', error);
        this.toastr.error('Failed to upload building image to storage');
      }
    );
  }

  private handleCustomerUploadResponse(response: any, type: ImageType) {
    const responseData = response.body;
    if (responseData?.status !== 200 && responseData?.status !== 'OK') {
      this.toastr.error(responseData?.message);
      return;
    }
    switch (type) {
      case ImageType.Bill:
        this.customer.billImageUrl = responseData.data.url;
        break;
      case ImageType.Card:
        this.customer.cardImageUrl = responseData.data.url;
        break;
      case ImageType.Meter:
        this.customer.meterImageUrl = responseData.data.url;
        break;
      case ImageType.MeterNumber:
        this.customer.meterNumberImageUrl = responseData.data.url;
        break;
      case ImageType.CardBackViewImage:
        this.customer.backCardViewImageUrl = responseData.data.url;
        break;
    }
    this.toastr.success('Successfully added image');
  }

  private handleBuildingUploadResponse(response: any, type: ImageType, imageId: number) {
    const responseData = response.body;
    if (responseData?.status !== 200 && responseData?.status !== 'OK') {
      this.toastr.error(responseData?.message);
      return;
    }
    if (imageId != -1) {
      const images = this.customer.buildingImages.filter(
        (x) => x.imageType == ImageType[type]
      );
      const imageIndex = this.customer.buildingImages.indexOf(images[imageId]);
      this.customer.buildingImages[imageIndex] = responseData.data;
    } else {
      this.customer.buildingImages.push(responseData.data);
    }
    this.toastr.success('Successfully added image');
  }

  // Image modal

  onModalFade() {
    this.currentRotation = 0;

    if (this.imgZoomFullContainer) {
      const displayValue = getComputedStyle(this.imgZoomFullContainer).display;
      if (displayValue == 'block') {
        this.imgElement.click();
      }
    }
  }

  onModalOpen() {
    this.renderer.setStyle(document.body, 'overflow', 'auto');
    this.renderer.setStyle(document.body, 'overflow-x', 'hidden');
    this.renderer.setStyle(document.body, 'padding-right', '0');
  }

  rotateLeft() {
    this.currentRotation -= 90;
  }

  rotateRight() {
    this.currentRotation += 90;
  }
}
